// ---------------------------------------------------------------------------
// IE 2026 08 29 / ODOO-5828 (заказ Игоря): подсветка ОСТАЛЬНЫХ вхождений выделенного
// текста — как «selection highlight» в VS Code. Повод: чтение терминала виджета
// («4.161s rendering …») и логов, где повторы ищутся глазами.
//
// Почему CSS Custom Highlight API, а не обёртки <mark> в DOM: гриды wijmo
// виртуализированы и пересобирают ячейки сами — любая наша мутация DOM ломает
// им разметку/замеры. Highlight API красит ДИАПАЗОНЫ, DOM не трогая вовсе.
// Нет API (старый браузер) — фича молча выключена.
//
// Скоуп подсветки — ближайший виджет (.WIDGET) от начала выделения: на столе
// десяток виджетов, глобальная подсветка была бы шумом.
// ---------------------------------------------------------------------------

import { cellMatch_hiddenText, cellMatch_zeroText } from '@/utils/cellMatch'   // IE 2026 09 20 / ODOO-5828: спрятанный текст (ноль, скрытый как пустое значение) не подсвечиваем

const NAME = 'sel-match'
const DEBOUNCE_MS = 140
const MIN_LEN = 1              // IE 2026 09 17 / ODOO-… (замечание Игоря): и один символ — «s» в редакторе кода подсвечивается, как «country»; порог — настройка fe.data.selectionHighlightMinLen
const MAX_LEN = 200
const MAX_MATCHES = 500        // больше — подсвечиваем первые N и не тормозим
const MAX_SCAN_CHARS = 400000  // кап на объём обходимого текста скоупа

let store = null
let timer = null

// IE 2026 09 05 / ODOO-336330: пороги — настройки базы (fe.ux.selectionHighlightDebounceMs, fe.data.selectionHighlight*);
// константы выше остаются кодовыми дефолтами
function cfg(key, def_) {
    try {
        const v = Number(store?.setting?.(key, def_))
        return v > 0 ? v : def_
    } catch (e) { return def_ }
}
let rafId = null
let scopeEl = null
let mo = null                  // MutationObserver текущего скоупа
let moTimer = null
let scrollEl = null

function supported() {
    return !!(window.CSS && window.CSS.highlights && typeof window.Highlight === 'function')
}

function on() {
    return !!store && store.selectionHighlight?.enabled !== false && supported()
}

export function clear() {
    try { window.CSS?.highlights?.delete(NAME) } catch (e) { /* no-op */ }
}

function detachObservers() {
    if (mo) { try { mo.disconnect() } catch (e) { /* no-op */ } mo = null }
    if (scrollEl) { scrollEl.removeEventListener('scroll', onScroll, true); scrollEl = null }
    scopeEl = null
}

// ближайший виджет → иначе прокручиваемый предок → иначе body
function findScope(node) {
    let el = node?.nodeType === 1 ? node : node?.parentElement
    const widget = el?.closest?.('.WIDGET')
    if (widget) return widget
    while (el && el !== document.body) {
        const st = getComputedStyle(el)
        if (/(auto|scroll)/.test(st.overflowY + st.overflowX) && el.scrollHeight > el.clientHeight + 4) return el
        el = el.parentElement
    }
    return document.body
}

// IE 2026 09 20 / ODOO-5828 (замечание Игоря: «при входе в ячейку вижу выделения нулей — в ячейках, в которых они были скрыты как пустые значения»):
// спрятанные значения для подсветки не существуют — их текст не красится и источником поиска они не служат.
// Выключатель — настройка базы fe.ux.highlightSkipHidden (false — прежнее поведение)
function skipHiddenOn() {
    try { return store?.setting?.('fe.ux.highlightSkipHidden', true) !== false } catch (e) { return true }
}
// Выделение живёт в РЕДАКТОРЕ ячейки со спрятанным нулём: при входе в правку грид сам выделяет значение целиком, Chrome отдаёт текст
// поля через getSelection().toString() (диапазон документа при этом схлопнут) — и все «0» виджета подсвечивались, хотя для
// пользователя ячейка пуста и он ничего не выделял. Условие узкое: редактор внутри ячейки cell-zero, выделено всё значение, и оно — ноль
function editorOfHiddenZero(sel, raw) {
    const ae = document.activeElement
    if (!ae || (ae.tagName !== 'INPUT' && ae.tagName !== 'TEXTAREA')) return false
    try { if (sel.rangeCount && !sel.getRangeAt(0).collapsed) return false } catch (e) { return false }   // выделен текст документа, не поля
    const cell = ae.closest?.('.wj-cell')
    if (!cell || !cell.classList.contains('cell-zero')) return false
    const v = String(ae.value ?? '').trim()
    return v === String(raw || '').trim() && cellMatch_zeroText(v)
}

// выделение пригодно к работе? (не схлопнуто, однострочное, не в поле ввода)
function selectionText(sel) {
    if (!sel || !sel.rangeCount) return ''
    const node = sel.anchorNode
    const host = node?.nodeType === 1 ? node : node?.parentElement
    // поля ввода: там своё поведение выделения — не вмешиваемся. Проверяем ТОЛЬКО узел
    // выделения: document.activeElement здесь бесполезен — в WebSCM почти всегда сфокусирован
    // скрытый input.wj-grid-editor (редактор ячейки wijmo), и проверка по нему глушила фичу везде
    if (host?.closest?.('input, textarea')) return ''
    // isCollapsed в этом приложении бывает true при непустом выделении (wijmo переносит фокус
    // в свой редактор сразу после dblclick) — источник истины: текст выделения
    const raw = String(sel.toString() || '')
    if (/[\r\n]/.test(raw)) return ''
    if (skipHiddenOn() && editorOfHiddenZero(sel, raw)) { if (store?._selectionHighlightStats) store._selectionHighlightStats.hiddenSource = (store._selectionHighlightStats.hiddenSource || 0) + 1; return '' }   // IE 2026 09 20 / ODOO-5828
    const text = raw.trim()
    if (text.length < cfg('fe.data.selectionHighlightMinLen', MIN_LEN) || text.length > MAX_LEN) return ''   // IE 2026 09 17 / ODOO-…: порог длины — настройка базы, дефолт 1
    return text
}

// диапазоны всех вхождений в скоупе, кроме самого выделенного фрагмента
function collectRanges(scope, needle, selRange) {
    const ranges = []
    const lower = needle.toLowerCase()
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
        acceptNode(n) {
            if (!n.nodeValue || n.nodeValue.length < needle.length) return NodeFilter.FILTER_REJECT
            const p = n.parentElement
            if (!p) return NodeFilter.FILTER_REJECT
            const tag = p.tagName
            if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'INPUT' || tag === 'TEXTAREA') return NodeFilter.FILTER_REJECT
            return NodeFilter.FILTER_ACCEPT
        },
    })
    let scanned = 0
    let node
    const maxScanChars = cfg('fe.data.selectionHighlightMaxScanChars', MAX_SCAN_CHARS)   // IE 2026 09 05 / ODOO-336330
    const maxMatches = cfg('fe.data.selectionHighlightMaxMatches', MAX_MATCHES)
    const hiddenMemo = skipHiddenOn() ? new WeakMap() : null   // IE 2026 09 20 / ODOO-5828: спрятанный текст не красим (проверка — только у узлов с совпадением)
    let hiddenSkipped = 0
    if (store?._selectionHighlightStats) store._selectionHighlightStats.hidden = 0
    while ((node = walker.nextNode())) {
        const val = node.nodeValue
        scanned += val.length
        if (scanned > maxScanChars) break
        const hay = val.toLowerCase()
        if (hiddenMemo && hay.indexOf(lower) >= 0 && cellMatch_hiddenText(node.parentElement, hiddenMemo)) {
            hiddenSkipped++
            if (store?._selectionHighlightStats) store._selectionHighlightStats.hidden = hiddenSkipped
            continue
        }
        let from = 0
        for (;;) {
            const i = hay.indexOf(lower, from)
            if (i < 0) break
            from = i + lower.length
            const r = document.createRange()
            try {
                r.setStart(node, i)
                r.setEnd(node, i + needle.length)
            } catch (e) { continue }
            // сам выделенный фрагмент браузер красит своим selection — не дублируем
            if (!(selRange && r.compareBoundaryPoints(Range.START_TO_START, selRange) === 0
                    && r.compareBoundaryPoints(Range.END_TO_END, selRange) === 0)) {
                ranges.push(r)
                if (ranges.length >= maxMatches) return ranges
            }
        }
    }
    return ranges
}

// пересчёт подсветки по текущему выделению (диапазоны протухают на виртуализации)
function repaint() {
    if (!on()) { clear(); return }
    const sel = window.getSelection()
    const text = selectionText(sel)
    if (!text) { clear(); detachObservers(); return }
    const selRange = sel.rangeCount ? sel.getRangeAt(0) : null
    const scope = scopeEl && scopeEl.isConnected ? scopeEl : findScope(sel.anchorNode)
    if (!scope) { clear(); return }
    const t0 = performance.now()
    const ranges = collectRanges(scope, text, selRange)
    try {
        if (ranges.length) window.CSS.highlights.set(NAME, new window.Highlight(...ranges))
        else clear()
    } catch (e) { clear() }
    const st = store._selectionHighlightStats
    if (st) {
        st.text = text.length > 40 ? text.slice(0, 40) + '…' : text
        st.matches = ranges.length
        st.ms = Math.round((performance.now() - t0) * 10) / 10
        st.scope = scope === document.body ? 'body' : (scope.className || scope.tagName || '').toString().slice(0, 40)
        st.repaints++
    }
    return ranges.length
}

function onScroll() {
    // прокрутка виртуализированного грида пересобирает ячейки — диапазоны протухают
    if (rafId) return
    rafId = requestAnimationFrame(() => { rafId = null; repaint() })
}

function attachObservers(scope) {
    detachObservers()
    scopeEl = scope
    scrollEl = scope
    scope.addEventListener('scroll', onScroll, true)
    try {
        mo = new MutationObserver(() => {
            if (moTimer) return
            moTimer = setTimeout(() => { moTimer = null; repaint() }, cfg('fe.ux.selectionHighlightDebounceMs', DEBOUNCE_MS))   // IE 2026 09 05 / ODOO-336330
        })
        mo.observe(scope, { childList: true, subtree: true, characterData: true })
    } catch (e) { mo = null }
}

function onSelectionChange() {
    if (!on()) { clear(); return }
    const sel = window.getSelection()
    // схлопнули выделение — гасим сразу, без ожидания дебаунса
    if (!sel || !String(sel.toString() || '').trim()) {
        if (timer) { clearTimeout(timer); timer = null }
        clear(); detachObservers()
        return
    }
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
        timer = null
        const s = window.getSelection()
        const text = selectionText(s)
        if (!text) { clear(); detachObservers(); return }
        const scope = findScope(s.anchorNode)
        if (scope !== scopeEl) attachObservers(scope)
        store._selectionHighlightStats.requests++
        repaint()
    }, cfg('fe.ux.selectionHighlightDebounceMs', DEBOUNCE_MS))   // IE 2026 09 05 / ODOO-336330
}

export function init(storeRef) {
    store = storeRef
    if (!supported()) {
        store._selectionHighlightStats.supported = false
        return false
    }
    store._selectionHighlightStats.supported = true
    document.addEventListener('selectionchange', onSelectionChange)
    return true
}

export function stats() {
    return { ...(store?._selectionHighlightStats || {}) }
}
